import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  DataTable,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Pagination,
  InlineLoading,
  InlineNotification,
  Grid,
  Column,
  Dropdown,
  Button,
  DatePicker,
  DatePickerInput,
} from "@carbon/react";

import { Filter } from "@carbon/icons-react";

import { callApi } from "../../../services/apiService";

const headers = [
  { key: "propertyId", header: "Property ID" },
  { key: "transactionType", header: "Transaction Type" },
  { key: "planName", header: "Plan Name" },
  { key: "baseAmount", header: "Base Amount" },
  { key: "gstPercentage", header: "GST %" },
  { key: "gstAmount", header: "GST Amount" },
  { key: "finalAmount", header: "Final Amount" },
  { key: "transactionDate", header: "Transaction Date" },
];



export default function GstReport() {
  const mountedRef = useRef(true);

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [planFilter, setPlanFilter] = useState(null);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState(null);

  const [planOptions, setPlanOptions] = useState([]);
  const [transactionTypeOptions, setTransactionTypeOptions] = useState([]);
  const [startDate, setStartDate] = useState(null);
const [endDate, setEndDate] = useState(null);

  

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

useEffect(() => {
  const t = setTimeout(() => {
    setDebouncedSearch(searchTerm.trim().toLowerCase());
  }, 350);

  return () => clearTimeout(t);
}, [searchTerm]);

useEffect(() => {
  let data = [...rows];

  // Search only inside the currently loaded/filter result
  if (debouncedSearch) {
    data = data.filter(
      (item) =>
        item.transactionType
          ?.toLowerCase()
          .includes(debouncedSearch) ||
        item.planName
          ?.toLowerCase()
          .includes(debouncedSearch)
    );
  }

  setFilteredRows(data);
  setPage(1);
}, [debouncedSearch, rows]);

const fetchGstFilterOptions = async () => {
  try {
    const response = await callApi(
      "GET",
      "/transactions/reports/gst-collection"
    );

    if (response?.status_code === 200) {
      const transactions = response.data.transactions || [];

      const transactionTypes = [
        ...new Set(
          transactions
            .map((item) => item.transaction_type)
            .filter(Boolean)
        ),
      ].map((item) => ({
        id: item,
        text: item,
      }));

      const plans = [
        ...new Set(
          transactions
            .map((item) => item.plan_name)
            .filter(Boolean)
        ),
      ].map((item) => ({
        id: item,
        text: item,
      }));

      setTransactionTypeOptions(transactionTypes);
      setPlanOptions(plans);
    }
  } catch (err) {
    console.error("Failed to load filter options:", err);
  }
};

const fetchGstReport = async () => {
  if (!mountedRef.current) return;

  setLoading(true);
  setError("");

  try {
    let url = "/transactions/reports/gst-collection";

const params = [];

if (transactionTypeFilter?.text) {
  params.push(
    `transaction_type=${encodeURIComponent(transactionTypeFilter.text)}`
  );
}

if (planFilter?.text) {
  params.push(
    `plan_name=${encodeURIComponent(planFilter.text)}`
  );
}

if (startDate) {
  params.push(`start_date=${formatDate(startDate)}`);
}

if (endDate) {
  params.push(`end_date=${formatDate(endDate)}`);
}
if (params.length > 0) {
  url += `?${params.join("&")}`;
}

console.log("Request URL:", url);
const response = await callApi("GET", url);

    console.log("GST Report:", response);

    if (response?.status_code === 200) {
      const transactions = response.data.transactions || [];
      
      // Transaction Type dropdown


// Plan Name dropdown


      const normalized = transactions.map(normalizeGstData);

setRows(normalized);
setFilteredRows(normalized);

  
    } else {
      setRows([]);
    }
  } catch (err) {
    console.error(err);
    setError("Failed to load GST report.");
    setRows([]);
  } finally {
    if (mountedRef.current) {
      setLoading(false);
    }
  }
}



useEffect(() => {
  mountedRef.current = true;

  fetchGstFilterOptions();
  fetchGstReport();

  return () => {
    mountedRef.current = false;
  };
}, []);




const paginatedRows = useMemo(() => {
  return filteredRows.slice(
    (page - 1) * pageSize,
    page * pageSize
  );
}, [filteredRows, page, pageSize]);

return (
  <>
    {/* Filter Section */}
    <div
      style={{
        padding: "1rem",
        backgroundColor: "#f4f4f4",
        borderRadius: "8px",
        marginBottom: "1rem",
        border: "1px solid #e0e0e0",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <Filter style={{ marginRight: "0.5rem" }} />
        <h4 style={{ margin: 0 }}>Filter GST Report</h4>
      </div>

      <Grid style={{ padding: 0 }}>
        {/* Plan Name */}
        <Column lg={4} md={4} sm={4}>
          <Dropdown
            id="plan-filter"
            items={planOptions}
            titleText="Plan Name"
            label="Choose"
            itemToString={(item) => item?.text || ""}
            selectedItem={planFilter}
            onChange={({ selectedItem }) =>
              setPlanFilter(selectedItem)
            }
            size="sm"
          />
        </Column>

        {/* Transaction Type */}
        <Column lg={4} md={4} sm={4}>
          <Dropdown
            id="transaction-type-filter"
            items={transactionTypeOptions}
            titleText="Transaction Type"
            label="Choose"
            itemToString={(item) => item?.text || ""}
            selectedItem={transactionTypeFilter}
            onChange={({ selectedItem }) =>
              setTransactionTypeFilter(selectedItem)
            }
            size="sm"
          />
        </Column>

{/* Start Date */}
<Column lg={4} md={4} sm={4}>
  <DatePicker
    datePickerType="single"
    value={startDate}
    onChange={(dates) => setStartDate(dates[0])}
  >
    <DatePickerInput
      id="gst-start-date"
      labelText="Start Date"
      placeholder="dd/mm/yyyy"
      size="sm"
    />
  </DatePicker>
</Column>

{/* End Date */}
<Column lg={4} md={4} sm={4}>
  <DatePicker
    datePickerType="single"
    value={endDate}
    onChange={(dates) => setEndDate(dates[0])}
  >
    <DatePickerInput
      id="gst-end-date"
      labelText="End Date"
      placeholder="dd/mm/yyyy"
      size="sm"
    />
  </DatePicker>
</Column>
      </Grid>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: "1rem",
          gap: "0.75rem",
        }}
      >
        <Button
  kind="secondary"
  onClick={async () => {
    setPlanFilter(null);
    setTransactionTypeFilter(null);
    setStartDate(null);
    setEndDate(null);
    setSearchTerm("");
    setPage(1);

    // Load all data again
    setLoading(true);

    try {
      const response = await callApi(
        "GET",
        "/transactions/reports/gst-collection"
      );

if (response?.status_code === 200) {
  const transactions = response.data.transactions || [];

  const normalized = transactions.map(normalizeGstData);

  setRows(normalized);

  setFilteredRows(normalized);


}
    } catch (err) {
      console.error(err);
      setError("Failed to load GST report.");
    } finally {
      setLoading(false);
    }
  }}
>
  Reset
</Button>

       <Button onClick={fetchGstReport}>
  Search
</Button>
      </div>
    </div>

    {/* DataTable */}
    <DataTable
      rows={paginatedRows}
      headers={headers}
    >
      {({
        rows,
        headers,
        getHeaderProps,
        getRowProps,
        getTableProps,
      }) => (
        <TableContainer title="GST Report">
          <TableToolbar>
            <TableToolbarContent>
              <TableToolbarSearch
  expanded
  placeholder="Search..."
  value={searchTerm}
onChange={(e) => {
  setSearchTerm(e.target.value);
}}
/>
            </TableToolbarContent>
          </TableToolbar>

          {loading && (
            <div style={{ padding: "2rem", textAlign: "center" }}>
              <InlineLoading description="Loading GST Report..." />
            </div>
          )}

          {error && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error}
              lowContrast
            />
          )}

          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableHeader
                    key={header.key}
                    {...getHeaderProps({ header })}
                  >
                    {header.header}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedRows.map((row) => (
                <TableRow
                  key={row.id}
                  {...getRowProps({ row })}
                >
                  <TableCell>
    {row.propertyId || "N/A"}
  </TableCell>

                  <TableCell>{row.transactionType}</TableCell>

                  <TableCell>
                    {row.planName || "N/A"}
                  </TableCell>

                  <TableCell>
                    {formatCurrency(row.baseAmount)}
                  </TableCell>

                  <TableCell>
                    {row.gstPercentage}%
                  </TableCell>

                  <TableCell>
                    {formatCurrency(row.gstAmount)}
                  </TableCell>

                  <TableCell>
                    {formatCurrency(row.finalAmount)}
                  </TableCell>

                  <TableCell>
                    {new Date(
                      row.transactionDate
                    ).toLocaleString("en-IN")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </DataTable>
        {rows.length > 0 && (
      <div
        style={{
          padding: "1rem",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Pagination
          totalItems={filteredRows.length}
          page={page}
          pageSize={pageSize}
          pageSizes={[10, 20, 50, 100]}
          onChange={({ page, pageSize }) => {
            setPage(page);
            setPageSize(pageSize);
          }}
        />
      </div>
    )}
  </>
);

}

const formatDate = (date) => {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatCurrency = (amount) => {
  if (amount == null) return "₹0.00";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(amount);
};
const normalizeGstData = (item) => ({
  id: String(item.transaction_id),

  propertyId: item.property_seq_id || "N/A",
  transactionType: item.transaction_type,
  planName: item.plan_name,

  baseAmount: item.base_amount,
  gstPercentage: item.gst_percentage,
  gstAmount: item.gst_amount,
  finalAmount: item.final_amount,

  transactionDate: item.transaction_date,
});
